import type { Metric } from "web-vitals";
import { supabase } from "@/integrations/supabase/client";

let buffer: Array<{
  metric_name: string;
  metric_value: number;
  metric_delta: number;
  metric_id: string;
  page_url: string;
  user_agent: string;
  user_id: string | null;
  tenant_id: string | null;
}> = [];

let flushTimer: ReturnType<typeof setTimeout> | null = null;
const FLUSH_INTERVAL = 10_000; // 10 seconds
const MAX_BUFFER_SIZE = 20;

async function flushBuffer() {
  if (buffer.length === 0) return;

  const batch = [...buffer];
  buffer = [];

  try {
    const { data: { user } } = await supabase.auth.getUser();
    const userId = user?.id ?? null;
    const tenantId = user?.user_metadata?.tenant_id ?? null;

    const rows = batch.map((m) => ({
      ...m,
      user_id: userId,
      tenant_id: tenantId,
    }));

    await supabase.from("web_vitals_metrics").insert(rows);
  } catch (e) {
    // Silently fail — don't break the app for telemetry
    console.debug("[web-vitals] flush failed", e);
  }
}

function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flushBuffer();
  }, FLUSH_INTERVAL);
}

function onMetric(metric: Metric) {
  buffer.push({
    metric_name: metric.name,
    metric_value: metric.value,
    metric_delta: metric.delta,
    metric_id: metric.id,
    page_url: window.location.pathname,
    user_agent: navigator.userAgent,
    user_id: null,
    tenant_id: null,
  });

  if (buffer.length >= MAX_BUFFER_SIZE) {
    flushBuffer();
  } else {
    scheduleFlush();
  }
}

export function initWebVitals() {
  import("web-vitals").then(({ onCLS, onINP, onFCP, onLCP, onTTFB }) => {
    onCLS(onMetric);
    onINP(onMetric);
    onFCP(onMetric);
    onLCP(onMetric);
    onTTFB(onMetric);
  });

  // Flush remaining metrics on page unload
  if (typeof window !== "undefined") {
    window.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") {
        flushBuffer();
      }
    });
  }
}
